import { useRef, useEffect, useState } from 'react';

const DemoPreview = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const slideIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Slideshow images (6 slides) - More prominent and relevant to ReceptionGenie services
  const slides = [
    'https://public.readdy.ai/ai/img_res/db0aa3d783fb846d88cbb9c1729f71be.jpg',
    'https://readdy.ai/api/search-image?query=Modern%20appointment%20scheduling%20calendar%20interface%20on%20computer%20screen%20showing%20booked%20time%20slots%20with%20professional%20office%20desk%20setup%20clean%20organized%20workspace%20business%20planning%20tools%20digital%20calendar%20application%20with%20confirmed%20appointments%20highlighted%20in%20pink%20and%20purple%20professional%20photography%20realistic%20business%20scene&width=980&height=220&seq=slide2v2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Happy%20business%20owner%20reviewing%20positive%20customer%20feedback%20and%20reviews%20on%20tablet%20device%20in%20modern%20office%20smiling%20satisfied%20entrepreneur%20checking%20five%20star%20ratings%20and%20testimonials%20bright%20professional%20workspace%20success%20and%20growth%20concept%20realistic%20business%20photography&width=980&height=220&seq=slide3v2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Professional%20customer%20service%20representative%20wearing%20headset%20providing%20helpful%20information%20to%20caller%20in%20modern%20call%20center%20bright%20friendly%20workspace%20with%20computer%20showing%20customer%20details%20warm%20welcoming%20atmosphere%20efficient%20communication%20realistic%20business%20photography%20high%20quality%20professional%20scene&width=980&height=220&seq=slide4v2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Business%20professional%20checking%20smartphone%20with%20incoming%20text%20message%20notification%20in%20modern%20office%20workspace%20mobile%20communication%20customer%20follow%20up%20messages%20clean%20contemporary%20setting%20professional%20attire%20realistic%20business%20photography%20natural%20lighting&width=980&height=220&seq=slide5v2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Successful%20business%20owner%20celebrating%20growth%20with%20upward%20trending%20charts%20and%20graphs%20on%20computer%20screens%20modern%20office%20environment%20showing%20revenue%20increase%20and%20business%20success%20metrics%20professional%20workspace%20achievement%20and%20results%20realistic%20business%20photography%20positive%20atmosphere&width=980&height=220&seq=slide6v2&orientation=landscape'
  ];

  // Poster image (shown before play) - More prominent and relevant
  const posterImage = 'https://readdy.ai/api/search-image?query=Professional%20friendly%20receptionist%20with%20headset%20ready%20to%20answer%20phone%20calls%20in%20modern%20bright%20office%20workspace%20welcoming%20smile%20customer%20service%20representative%20at%20organized%20desk%20with%20computer%20and%20phone%20system%20clean%20contemporary%20business%20environment%20high%20quality%20realistic%20photography%20warm%20professional%20atmosphere&width=980&height=220&seq=posterv2&orientation=landscape';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const fitCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    fitCanvas();
    window.addEventListener('resize', fitCanvas);

    return () => {
      window.removeEventListener('resize', fitCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    };
  }, []);

  // Start slideshow when audio plays
  useEffect(() => {
    if (isPlaying) {
      // Advance slide every 5 seconds while playing (slowed from 2.5s)
      slideIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
    } else {
      // Stop slideshow when paused
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
        slideIntervalRef.current = null;
      }
    }

    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    };
  }, [isPlaying, slides.length]);

  const draw = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dataArray = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(dataArray);

    const w = canvas.getBoundingClientRect().width;
    const h = canvas.getBoundingClientRect().height;

    ctx.clearRect(0, 0, w, h);

    const mid = h / 2;
    const slice = w / dataArray.length;

    // Enhanced waveform with strong contrast against photos
    const gradient = ctx.createLinearGradient(0, 0, w, 0);
    gradient.addColorStop(0, '#ec4899');
    gradient.addColorStop(0.5, '#a855f7');
    gradient.addColorStop(1, '#ec4899');

    ctx.lineWidth = 5;
    ctx.strokeStyle = gradient;
    ctx.shadowColor = '#ec4899';
    ctx.shadowBlur = 35;

    ctx.beginPath();
    let x = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const v = dataArray[i] / 128.0;
      const y = mid + (v - 1) * (mid * 0.55);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      x += slice;
    }
    ctx.stroke();

    animationFrameRef.current = requestAnimationFrame(draw);
  };

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;

        sourceRef.current = audioContextRef.current.createMediaElementSource(audio);
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);
      }

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      if (audio.paused) {
        await audio.play();
        setIsPlaying(true);
        draw();
      } else {
        audio.pause();
        setIsPlaying(false);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      }
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  };

  return (
    <section id="demo" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            See ReceptionGenie in Action
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Watch how our AI genie handles real customer calls with professionalism and efficiency
          </p>
        </div>

        {/* Listen to a Real Call Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-3">
              Listen to a Real Call
            </h3>
            <p className="text-lg text-gray-600">
              Press play to hear how ReceptionGenie answers calls and captures leads automatically.
            </p>
          </div>

          <div className="max-w-[980px] mx-auto p-6 rounded-2xl bg-gradient-to-br from-pink-50 via-purple-50 to-pink-50 border border-pink-200 shadow-2xl relative overflow-hidden">
            {/* Animated glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-pink-400/20 via-purple-400/20 to-pink-400/20 opacity-50 blur-3xl pointer-events-none"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between gap-3 mb-4">
                <button
                  onClick={togglePlay}
                  className="whitespace-nowrap bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-6 py-2.5 rounded-full font-bold transition-all cursor-pointer text-sm shadow-lg"
                >
                  {isPlaying ? '⏸ Playing a Real Customer Call' : '▶︎ Hear a Real Call'}
                </button>
                <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-4 py-2 rounded-full font-extrabold text-sm shadow-lg">
                  24/7
                </div>
              </div>

              <div className="relative bg-white rounded-xl p-6 shadow-inner overflow-hidden">
                {/* Background slideshow container */}
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  {/* Poster image (shown before play) */}
                  <div 
                    className={`absolute inset-0 transition-opacity duration-600 ${!isPlaying ? 'opacity-100' : 'opacity-0'}`}
                    style={{
                      backgroundImage: `url(${posterImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                  
                  {/* Slideshow images (shown while playing) */}
                  {isPlaying && slides.map((slide, index) => (
                    <div
                      key={index}
                      className="absolute inset-0 transition-opacity duration-600"
                      style={{
                        backgroundImage: `url(${slide})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: currentSlide === index ? 1 : 0,
                        animation: currentSlide === index ? 'subtleZoom 5s ease-out' : 'none'
                      }}
                    />
                  ))}
                  
                  {/* Lighter overlay - let images show through more */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/15"></div>
                </div>

                <canvas
                  ref={canvasRef}
                  className="w-full h-[220px] rounded-xl block relative z-10"
                />
              </div>

              {/* Credibility text */}
              <div className="text-center mt-3 text-sm text-gray-500">
                This is an actual call answered by ReceptionGenie. No scripting.
              </div>

              <audio
                ref={audioRef}
                preload="auto"
                crossOrigin="anonymous"
              >
                <source src="https://storage.googleapis.com/msgsndr/ufGgg1PmwgWzIDRrG0qr/media/695dd0b90597df70a36eb8ee.wav" type="audio/wav" />
              </audio>
            </div>
          </div>

          {/* Natural CTA progression */}
          <div className="text-center mt-6">
            <p className="text-gray-700 text-lg">
              Want to hear it live?{' '}
              <a 
                href="tel:+18886009252" 
                className="text-pink-600 hover:text-pink-700 font-bold underline decoration-2 underline-offset-2 cursor-pointer transition-colors"
              >
                Call the demo line: +1-888-600-9252
              </a>
            </p>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Demo Visual */}
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8 shadow-xl">
            <div className="space-y-6">
              {/* Call Flow */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-fill text-green-600 text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-2">Incoming Call</div>
                    <div className="text-sm text-gray-600">Customer calling about services...</div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center">
                <i className="ri-arrow-down-line text-pink-500 text-2xl"></i>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <img 
                    src="https://static.readdy.ai/image/f188085399ab07ac2c79984c802b33af/d7137dec17d438e55dac1175e416585f.png" 
                    alt="Genie" 
                    className="w-12 h-12 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-2">Genie Answers</div>
                    <div className="text-sm text-gray-600">"Hello! Thank you for calling. How can I help you today?"</div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center">
                <i className="ri-arrow-down-line text-pink-500 text-2xl"></i>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-fill text-blue-600 text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-2">Appointment Confirmed</div>
                    <div className="text-sm text-gray-600">Scheduled for Tuesday at 2:00 PM</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-pink-100 rounded-xl p-4 text-center">
                <div className="text-sm font-semibold text-pink-700">
                  <i className="ri-line-chart-line mr-2"></i>
                  Lead Captured &amp; Converted
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Want to Hear Genie in Action?
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Call our demo line right now and experience how ReceptionGenie handles customer inquiries with natural conversation and professional service.
              </p>
              
              <div className="bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-8 text-white">
                <div className="text-sm font-semibold mb-2">Call Now to Test</div>
                <a 
                  href="tel:+18886009252"
                  className="text-4xl font-bold mb-4 block hover:opacity-90 transition-opacity cursor-pointer"
                >
                  +1 888-600-9252
                </a>
                <div className="text-sm opacity-90">Available 24/7 - Try it anytime!</div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border-2 border-pink-200 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-pink-600 mb-1">2 sec</div>
                <div className="text-sm text-gray-600">Average Answer Time</div>
              </div>
              <div className="bg-white border-2 border-pink-200 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-pink-600 mb-1">100%</div>
                <div className="text-sm text-gray-600">Calls Answered</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add subtle zoom animation */}
      <style>{`
        @keyframes subtleZoom {
          from {
            transform: scale(1);
          }
          to {
            transform: scale(1.05);
          }
        }
      `}</style>
    </section>
  );
};

export default DemoPreview;